import ServiceWrapper from 'Services/ServiceWrapper';
import AuthManager from 'Common/AuthManager';
import { runtimeConfig } from 'config/runtimeConfig';

const GMS_BACKEND_URL = runtimeConfig.GMS_BACKEND_API_URL;

export type ParentChannelOption = {
    id: string;
    name: string;
};

export type EnvironmentOption = {
    id: string;
    name: string;
    key: string;
};

type ServiceResult<T> = { success: boolean; data?: T; error?: string };

/**
 * Fetch the parent channels ("Channel" column) for the current party + domain.
 * Mirrors the listing used by the global search bar hierarchy.
 */
export const fetchParentChannels = async (
    partyId: string,
    domainId: string
): Promise<ServiceResult<ParentChannelOption[]>> => {
    try {
        const response = await ServiceWrapper.doGet(
            `${GMS_BACKEND_URL}list/party/${partyId}/industry/${domainId}/parent_channel`,
            null,
            null,
            AuthManager.getGmsToken()
        );

        const body = response?.data;
        if (response?.errorfound || !body?.data || !Array.isArray(body.data)) {
            return { success: false, error: 'Failed to fetch channels' };
        }

        const channels: ParentChannelOption[] = body.data.map((channel: any) => ({
            id: channel.id,
            name: channel.name,
        }));

        return { success: true, data: channels };
    } catch (error) {
        return {
            success: false,
            error: error instanceof Error ? error.message : 'Unknown error',
        };
    }
};

/**
 * Fetch the environments (child channels) grouped under a parent channel.
 * The child channel `key` is the SKU-mapping target used for sync.
 */
export const fetchEnvironments = async (
    partyId: string,
    parentChannelId: string
): Promise<ServiceResult<EnvironmentOption[]>> => {
    try {
        const response = await ServiceWrapper.doGet(
            `${GMS_BACKEND_URL}list/party/${partyId}/parent_channel/${parentChannelId}/channel`,
            null,
            null,
            AuthManager.getGmsToken()
        );

        const body = response?.data;
        if (response?.errorfound || !body?.data || !Array.isArray(body.data)) {
            return { success: false, error: 'Failed to fetch environments' };
        }

        const environments: EnvironmentOption[] = body.data.map((env: any) => ({
            id: env.id,
            name: env.name,
            key: env.key,
        }));

        return { success: true, data: environments };
    } catch (error) {
        return {
            success: false,
            error: error instanceof Error ? error.message : 'Unknown error',
        };
    }
};
